import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  type AppLocale,
  isRtlLocale,
  LOCALE_LABELS,
  normalizeLocale,
  SUPPORTED_LOCALES,
} from '../i18n/config';
import { applyDocumentLocale, mergeRemoteTranslations } from '../i18n/init';
import { getPlatformStore } from '../platform';
import { bridgeLocales, setApiLocale } from '../services/api';
import { LocaleContext, type LocaleContextValue } from './locale-context';

export function LocaleProvider({ children }: { children: React.ReactNode }) {
  const { i18n } = useTranslation();
  const [locale, setLocaleState] = useState<AppLocale>(normalizeLocale(i18n.language));
  const [ready, setReady] = useState(false);

  const loadRemoteBundle = useCallback(async (lng: AppLocale) => {
    setApiLocale(lng);
    try {
      const res = await bridgeLocales.fetch(lng);
      const payload = res.data?.data;
      if (payload?.content && typeof payload.content === 'object') {
        mergeRemoteTranslations(lng, payload.content as Record<string, unknown>);
        await getPlatformStore().setLocaleCache(lng, {
          version: payload.version ?? 1,
          content: payload.content,
        });
      }
    } catch {
      // Bundled JSON remains when API is unreachable.
    }
  }, []);

  useEffect(() => {
    let cancelled = false;

    void (async () => {
      const lng = normalizeLocale(i18n.language);
      applyDocumentLocale(lng);
      setLocaleState(lng);

      const cached = await getPlatformStore().getLocaleCache(lng);
      if (cached?.content) {
        mergeRemoteTranslations(lng, cached.content as Record<string, unknown>);
      }
      await loadRemoteBundle(lng);
      if (!cancelled) {
        setReady(true);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [i18n.language, loadRemoteBundle]);

  const setLocale = useCallback(
    async (next: AppLocale) => {
      const lng = normalizeLocale(next);
      await i18n.changeLanguage(lng);
      applyDocumentLocale(lng);
      setLocaleState(lng);
      await getPlatformStore().setLocale(lng);
      await loadRemoteBundle(lng);
    },
    [i18n, loadRemoteBundle],
  );

  const refreshFromApi = useCallback(async () => {
    await loadRemoteBundle(locale);
  }, [loadRemoteBundle, locale]);

  const value = useMemo<LocaleContextValue>(
    () => ({
      locale,
      dir: isRtlLocale(locale) ? 'rtl' : 'ltr',
      isRtl: isRtlLocale(locale),
      labels: LOCALE_LABELS,
      supportedLocales: SUPPORTED_LOCALES,
      ready,
      setLocale,
      refreshFromApi,
    }),
    [locale, ready, setLocale, refreshFromApi],
  );

  return (
    <LocaleContext.Provider value={value}>
      {!ready ? (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100vh',
          }}
        >
          <span style={{ color: 'var(--fg-3)', fontSize: 14 }}>…</span>
        </div>
      ) : (
        children
      )}
    </LocaleContext.Provider>
  );
}
